import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyBusiness = {
  business_name: '', address: '', phone: '', email: '', vat_no: '', reg_no: '',
  logo_data_url: '',
  bank_name: '', bank_account_name: '', bank_account_number: '', bank_branch_code: '', bank_swift: '', bank_account_type: '',
};

const PartnerProfile = ({ user }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [business, setBusiness] = useState(emptyBusiness);
  const [businessSaving, setBusinessSaving] = useState(false);
  const [businessDirty, setBusinessDirty] = useState(false);

  useEffect(() => {
    axios.get(`${API}/partner/profile`, { withCredentials: true })
      .then((r) => setBusiness({ ...emptyBusiness, ...(r.data || {}) }))
      .catch(() => {});
  }, []);

  const setB = (k, v) => { setBusiness({ ...business, [k]: v }); setBusinessDirty(true); };

  const onLogoFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 200 * 1024) return toast.error('Logo too large — please use an image under 200 KB');
    const reader = new FileReader();
    reader.onload = () => { setB('logo_data_url', reader.result); };
    reader.readAsDataURL(f);
  };

  const saveBusiness = async () => {
    setBusinessSaving(true);
    try {
      await axios.put(`${API}/partner/profile`, business, { withCredentials: true });
      toast.success('Business profile saved');
      setBusinessDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setBusinessSaving(false);
    }
  };
  const [submitting, setSubmitting] = useState(false);

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="partner-profile">
      <div className="bg-black/40 backdrop-blur-sm rounded-xl p-8 border border-gray-700">
        <h2 className="text-2xl font-bold text-white mb-6">Profile Settings</h2>

        {/* User Info */}
        <div className="mb-8 p-4 bg-ewss-navy/50 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Account Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Company:</span>
              <span className="text-white font-medium">{user?.company}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Name:</span>
              <span className="text-white">{user?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Email:</span>
              <span className="text-white">{user?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Role:</span>
              <span className="text-ewss-cyan capitalize">{user?.role}</span>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">Change Password</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Current Password *
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan"
                placeholder="Enter current password"
                data-testid="partner-profile-current-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">New Password *</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan"
                placeholder="Enter new password (min 6 characters)"
                data-testid="partner-profile-new-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirm New Password *
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan"
                placeholder="Confirm new password"
                data-testid="partner-profile-confirm-password"
              />
            </div>

            <button
              onClick={handlePasswordChange}
              disabled={submitting}
              className="w-full px-6 py-3 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 transition-colors disabled:opacity-60"
              data-testid="partner-profile-update-password-button"
            >
              {submitting ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </div>

        <div className="text-sm text-gray-400 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <p className="font-semibold text-blue-400 mb-2">🔒 Password Requirements:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Minimum 6 characters</li>
            <li>Use a unique password not used elsewhere</li>
            <li>Keep your password secure and private</li>
          </ul>
        </div>

        {/* Business Profile — used on client-facing quote PDFs */}
        <div className="mt-8 pt-6 border-t border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-1">Business Profile</h3>
          <p className="text-gray-400 text-xs mb-4">These details appear on client-facing quote PDFs (not on your EWSS order requests).</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Business (trading) name" value={business.business_name} onChange={(e) => setB('business_name', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" data-testid="business-name" />
            <input placeholder="Business email" value={business.email} onChange={(e) => setB('email', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
            <input placeholder="Phone" value={business.phone} onChange={(e) => setB('phone', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
            <input placeholder="Physical address" value={business.address} onChange={(e) => setB('address', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
            <input placeholder="VAT number" value={business.vat_no} onChange={(e) => setB('vat_no', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
            <input placeholder="Company reg number" value={business.reg_no} onChange={(e) => setB('reg_no', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
          </div>

          <div className="mt-4">
            <label className="block text-sm text-gray-300 mb-2">Company Logo (PNG/JPG, max 200 KB — shown on client quotes)</label>
            <input type="file" accept="image/*" onChange={onLogoFile} className="text-white text-sm" data-testid="business-logo-upload" />
            {business.logo_data_url && (
              <div className="mt-3 flex items-center gap-3">
                <img src={business.logo_data_url} alt="Logo preview" className="h-16 bg-white/10 p-2 rounded" />
                <button onClick={() => setB('logo_data_url', '')} className="text-red-400 text-xs hover:underline">Remove</button>
              </div>
            )}
          </div>

          <h4 className="text-sm font-semibold text-gray-300 mt-6 mb-2">Banking Details (optional, shown on client quotes)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Bank" value={business.bank_name} onChange={(e) => setB('bank_name', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
            <input placeholder="Account name" value={business.bank_account_name} onChange={(e) => setB('bank_account_name', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
            <input placeholder="Account number" value={business.bank_account_number} onChange={(e) => setB('bank_account_number', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
            <input placeholder="Account type (Cheque / Savings)" value={business.bank_account_type} onChange={(e) => setB('bank_account_type', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
            <input placeholder="Branch code" value={business.bank_branch_code} onChange={(e) => setB('bank_branch_code', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
            <input placeholder="Swift code" value={business.bank_swift} onChange={(e) => setB('bank_swift', e.target.value)} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
          </div>

          <button onClick={saveBusiness} disabled={businessSaving || !businessDirty} className="mt-4 px-6 py-2 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 disabled:opacity-50" data-testid="business-save-button">
            {businessSaving ? 'Saving…' : 'Save Business Profile'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartnerProfile;
